// ─────────────────────────────────────────────────────────────────────────────
// SATIŞ TERMİNALİ OTURUM BAĞLAMI
//
// local-service, kaydedilmiş 11 haneli bir satış terminali kodunu
// `/#/cihaz-oturum/<kod>` derin bağlantısıyla açar. Bootstrap rotası kodu gerçek
// bir own-auth oturumuna çevirir (bkz. ownAuthSession.loginTerminal) ve hedef
// satış ekranına yönlendirir. Bu modül, o yönlendirmeden SONRA hangi ekranın
// hangi kodla açıldığını hatırlar.
//
// Bağlam bilerek JWT claim'i olarak taşınmıyor: /api/auth/refresh yeni access
// token'ı GenerateAccessToken(user) ile üretiyor, oraya eklenen özel bir claim
// ilk yenilemede kaybolurdu.
//
// ⚠️ DAİRESEL İMPORT: bu modül `ownAuthSession`'ı import eder, tersi DEĞİL.
// `loginTerminal` bağlamı yazmaz; yazma işini bootstrap bileşeni yapar.
// ─────────────────────────────────────────────────────────────────────────────
import { getCurrentUid } from './ownAuthSession';

/** Cihaz kaydındaki DeviceMode değerleri (backend: Entities.Models.SalesTerminal). */
export type TerminalDeviceMode =
  | 'sales_quicksale'
  | 'sales_restaurant'
  | 'sales_package'
  | 'sales_pyk';

export interface TerminalContext {
  /** 11 haneli satış terminali kodu. */
  screenCode: string;
  deviceMode: TerminalDeviceMode;
  /**
   * Cihaz kaydındaki DeviceScreens.LinkedScreenId. Her mod kendi tablosunu
   * işaret eder (bkz. DeviceController.ResolveSalesTerminalTargetAsync):
   * sales_quicksale → QuickSaleScreens.Id, sales_restaurant → RestaurantScreens.Id
   * (o ekranın QuickSaleScreenId'si DEĞİL), sales_package → PackageScreens.Id,
   * sales_pyk → PykScreens.Id.
   */
  linkedScreenId: number;
  /**
   * Rotaya yazılacak Id. Yalnızca paket modunda `linkedScreenId`'den farklıdır:
   * /paket-ekrani/:screenId rotası QuickSaleScreens.Id bekler (PackageOrderScreen
   * bu Id ile ScreenType='Package' satırını arar), cihaz kaydı ise
   * PackageScreens.Id tutar. Çeviriyi TerminalSessionBootstrap yapıyor; iki
   * tablonun Id dizileri bağımsız ilerlediği için "önce şunu, olmazsa bunu dene"
   * yaklaşımı yanlış ekranı açabilirdi.
   */
  routeScreenId?: number;
  /**
   * Cihazın kendi adı (kod üretilirken girilen isim). Bağlı satış ekranının adı
   * değil: o kayıt kiracı veritabanında ve token veren gts_auth_api yalnızca
   * merkezi DB'yi görüyor. Kullanıcıya gösterilen etiket her yerde cihaz adı.
   */
  screenName?: string;
  branchId?: string | null;
}

const TERMINAL_CONTEXT_KEY = 'gts_terminal_context';

/** Bootstrap rotasının yolu (App.tsx'te guard'sız tanımlı). */
export const TERMINAL_BOOTSTRAP_PATH = '/cihaz-oturum';

const VALID_MODES: TerminalDeviceMode[] = [
  'sales_quicksale',
  'sales_restaurant',
  'sales_package',
  'sales_pyk',
];

export function setTerminalContext(ctx: TerminalContext): void {
  try {
    localStorage.setItem(TERMINAL_CONTEXT_KEY, JSON.stringify(ctx));
  } catch (e) {
    console.warn('[terminalSession] Bağlam yazılamadı:', e);
  }
}

/**
 * Saklanan terminal bağlamını döndürür. Bozuk/eksik kayıtlarda null döner —
 * yarım bir bağlam, ekran erişim kontrolünü yanlış dala sokabileceği için
 * yok sayılıyor.
 */
export function getTerminalContext(): TerminalContext | null {
  try {
    const raw = localStorage.getItem(TERMINAL_CONTEXT_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<TerminalContext>;
    if (
      typeof parsed?.screenCode !== 'string' ||
      !parsed.screenCode ||
      typeof parsed?.linkedScreenId !== 'number' ||
      !VALID_MODES.includes(parsed?.deviceMode as TerminalDeviceMode)
    ) {
      return null;
    }

    return parsed as TerminalContext;
  } catch {
    return null;
  }
}

export function clearTerminalContext(): void {
  try {
    localStorage.removeItem(TERMINAL_CONTEXT_KEY);
  } catch {
    /* yok say */
  }
}

/**
 * Şu anda gerçekten bir terminal oturumunda mıyız?
 *
 * Yalnızca bağlamın VARLIĞINA bakmak yetmez: AuthContext.logout() tenant
 * anahtarlarını tarayıp silerken `gts_terminal_context`'e dokunmuyor (dokunmaması
 * gerekiyor, çıkışta aynı koda geri bağlanabilmemiz için). Dolayısıyla aynı
 * tarayıcıda sonradan normal bir kullanıcı giriş yaparsa bayrak geride kalır.
 * Bu yüzden oturumdaki kimliğin de o koda ait terminal kullanıcısı olması
 * şart koşuluyor — bayat bağlam normal oturumu etkileyemez.
 */
export function isTerminalSession(): boolean {
  const ctx = getTerminalContext();
  if (!ctx) return false;
  return getCurrentUid() === `terminal_${ctx.screenCode}`;
}

/** Terminal oturumundaysa bağlamı, değilse null döndürür. */
export function getActiveTerminalContext(): TerminalContext | null {
  return isTerminalSession() ? getTerminalContext() : null;
}

/** Kodun bootstrap rotası: local-service bu adresi açar, çıkış da buraya döner. */
export function getTerminalBootstrapRoute(screenCode: string): string {
  return `${TERMINAL_BOOTSTRAP_PATH}/${encodeURIComponent(screenCode)}`;
}

/**
 * Çıkıştan sonra gidilecek rota.
 *
 * Terminal oturumunda `/login`'e gitmek terminali kullanılamaz hale getirir:
 * makinede e-posta/şifre yok, yalnızca kod var. Bunun yerine bootstrap rotasına
 * dönülür — pratikte "kasiyeri düşür, ekranı yeniden bağla" anlamına gelir.
 *
 * ⚠️ ÇIKIŞTAN ÖNCE çağrılmalı: `isTerminalSession()` oturumdaki kimliğe bakıyor
 * ve `logout()` o kimliği siliyor. Çıkıştan sonra çağrılırsa her zaman `/login`
 * döner.
 */
export function getPostLogoutRoute(): string {
  const ctx = getActiveTerminalContext();
  return ctx ? getTerminalBootstrapRoute(ctx.screenCode) : '/login';
}

/**
 * Cihaz moduna göre açılacak satış ekranı rotası. Parametre adları App.tsx'teki
 * gerçek rota tanımlarından geliyor.
 */
export function getTerminalTargetRoute(ctx: TerminalContext): string {
  const screenId = ctx.routeScreenId ?? ctx.linkedScreenId;

  switch (ctx.deviceMode) {
    case 'sales_quicksale':
      return `/hizli-satis/ekran/${screenId}`;
    case 'sales_pyk':
      return `/pyk-ekrani/${screenId}`;
    case 'sales_restaurant':
      return `/restaurant-screen/${screenId}`;
    case 'sales_package':
      return `/paket-ekrani/${screenId}`;
  }
}
